import { Col, Pagination, Row, Space, Spin, Switch, Table } from 'antd';
import React, { useState } from 'react';
import {
    CheckCircleOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    NodeIndexOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons';
import { useCurrentRider } from '../hooks/useCurrentRider';
import { useSortedRiderQuery } from '../hooks/useSortedRiderQuery';
import { useUpdateRiderMutation } from '../../../GraphQL/hooks/useUpdateRiderMutation';
import { RidersFilter } from './RidersFilter';
import RiderViewModal from './RiderViewModal';
import { Rider } from '../../../shared/types';
import RiderDetailsDrawer from './Drawer/RiderDetailsDrawer';
import { useMutation } from '@apollo/client';
import PopConfirm from '../../../shared/Components/PopConfirm';
import { CITRIN_DELETE_RIDER, DeleteRiderRequest, DeleteRiderResponse } from '../../../GraphQL';
import { convertDateToFormat } from '../../../shared/Utils';

const RiderTable = () => {
    const { sortedRiders: riders, refetch, loading, ...pagination } = useSortedRiderQuery();
    const [deleteMutation] = useMutation<DeleteRiderResponse, DeleteRiderRequest>(CITRIN_DELETE_RIDER);
    const [isViewModalVisible, setViewModalVisible] = useState(false);
    const { setCurrentRider } = useCurrentRider();
    const [isDrawerVisible, setDrawerVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleViewRider = (rider) => {
        setCurrentRider(rider);
        setViewModalVisible(true);
    };

    const handleViewRiderTrips = (rider) => {
        setCurrentRider(rider);
        setDrawerVisible(true);
    };
    const updateIdentityRiderMutation = useUpdateRiderMutation();

    const handleUpdateIdentityStatusRider = async (rider: Rider, checked: boolean) => {
        setIsLoading(true);
        try {
            await updateIdentityRiderMutation({
                _id: rider._id,
                identityCheck: checked,
            });
            refetch();
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveRider = (rider: Rider) => {
        setIsLoading(true);
        deleteMutation({ variables: { id: rider._id } })
            .then(() => {
                refetch();
            })
            .catch((err) => console.error(err))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const columns = [
        {
            title: 'Rider Name',
            dataIndex: 'fullName',
            key: 'fullName',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Register Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            sorter: (a, b) => a.createdDate - b.createdDate,
            render: (text, record) => {
                return convertDateToFormat(record.createdDate, 'DD-MM-YYYY, HH:mm');
            },
        },
        {
            title: 'Phone number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        },
        {
            title: 'Signing method',
            dataIndex: 'signingMethod',
            key: 'signingMethod',
        },
        {
            title: 'AppVersion',
            dataIndex: 'mobileAppVersion',
            key: 'mobileAppVersion',
        },
        {
            title: 'Identity Check',
            dataIndex: 'identityCheck',
            key: 'identityCheck',
            render: (text, record: Rider) => {
                return record.identityCheck ? (
                    <CheckCircleOutlined title="Checked" style={{ fontSize: '16px', color: 'green' }} />
                ) : (
                    <ExclamationCircleOutlined title="Not checked" style={{ fontSize: '16px', color: 'red' }} />
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record: Rider) => (
                <Space size="middle">
                    <a href="#" onClick={() => handleViewRiderTrips(record)} style={{ color: '#1890ff' }}>
                        <NodeIndexOutlined />
                    </a>
                    <a href="#" onClick={() => handleViewRider(record)}>
                        <EyeOutlined />
                    </a>
                    <PopConfirm
                        title="Update Identity status rider"
                        description="Are you sure to update this rider identity status?"
                        onConfirmTask={() => handleUpdateIdentityStatusRider(record, !record.identityCheck)}
                        popIcon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        actionIcon={
                            <Switch
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                                checked={record.identityCheck}
                                style={{
                                    backgroundColor: record.identityCheck ? '#52c41a' : '#ff4d4f',
                                }}
                            />
                        }
                    />
                    <PopConfirm
                        title={'Delete the Rider'}
                        description={'Are you sure to delete this Rider?'}
                        onConfirmTask={() => handleRemoveRider(record)}
                        popIcon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        actionIcon={<DeleteOutlined style={{ fontSize: '16px', color: 'red' }} />}
                    />
                </Space>
            ),
        },
    ];

    const Paginator = () => (
        <Row justify={'center'}>
            <Col>
                <Pagination
                    size="default"
                    total={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    current={pagination.page}
                    showTotal={(total) => `Total Riders ${total}`}
                    onChange={(page, pageSize) => {
                        pagination.setPage(page);
                    }}
                    showQuickJumper={true}
                    showSizeChanger={false}
                    showLessItems={false}
                />
            </Col>
        </Row>
    );

    return (
        <>
            {isViewModalVisible && <RiderViewModal isOpen={isViewModalVisible} onRequestClose={setViewModalVisible} />}
            {isDrawerVisible && <RiderDetailsDrawer open={isDrawerVisible} onClose={() => setDrawerVisible(false)} />}

            <div style={{ flex: '1 1 auto' }}>
                <Row gutter={[16, 6]} style={{ paddingBottom: 10, paddingTop: 20 }}>
                    <RidersFilter />
                </Row>

                <Row gutter={16} style={{ paddingBottom: 10, paddingTop: 10 }}>
                    <Col xs={24}>
                        <Spin spinning={loading || isLoading}>
                            <Table
                                dataSource={riders}
                                columns={columns}
                                pagination={false}
                                rowKey={() => Math.random().toString()}
                                scroll={{ x: 1500 }}
                            ></Table>
                        </Spin>
                    </Col>
                </Row>
                <Paginator />
            </div>
        </>
    );
};

export default RiderTable;
